import { useMemo } from "react";

/* Animated Mayan Tzolk'in badge — a carved-stone seal, deliberately distinct
   from the Constellation. A jade cartouche with a slowly rotating notched ring
   and a glowing day-sign glyph. Built to double as a home-web satellite node
   (like StandStarGlyph next to Values), so it reads at small sizes too.

   Modes:
   - outline: dim, for an un-activated satellite node.
   - default: lit, steady slow rotation + glow pulse (result + activated node).

   Day-sign glyphs: imix (river / primal source) is fully drawn; the other 19
   fall back to a generic etched mark until each gets a bespoke glyph. */

const JADE = "#6DA88F";
const LIME = "#D4C4A0";

// Bespoke day-sign glyphs. Each draws inside a 0..100 box, centered ~(50,50),
// as an etched line motif evocative of the sign's nature. Consistent stroke
// weight so they read as one carved set, at hero and satellite sizes alike.
const GLYPHS = {
  // Imox (Imix) — Water / crocodile: a source-well feeding flowing water.
  imix: (
    <>
      <circle cx="50" cy="30" r="9" />
      <circle cx="50" cy="30" r="3.4" fill={LIME} stroke="none" />
      <path d="M20 48 C30 41, 40 55, 50 48 C60 41, 70 55, 80 48" />
      <path d="M18 60 C29 53, 39 67, 50 60 C61 53, 71 67, 82 60" opacity="0.85" />
      <path d="M20 72 C30 65, 40 79, 50 72 C60 65, 70 79, 80 72" opacity="0.7" />
    </>
  ),
  // Ik' — Wind / breath: the T-shaped wind window + a breath curl.
  ik: (
    <>
      <path d="M30 32 H70 V44 H58 V74 H42 V44 H30 Z" />
      <path d="M74 40 q9 7 2 16" opacity="0.6" />
    </>
  ),
  // Aq'ab'al (Ak'b'al) — Dawn: the sun's first arc on the horizon.
  akbal: (
    <>
      <path d="M24 66 H76" />
      <path d="M34 66 A16 16 0 0 1 66 66" fill={JADE} fillOpacity="0.18" />
      <path d="M50 40 V32 M33 47 l-6 -5 M67 47 l6 -5" opacity="0.7" />
      <circle cx="50" cy="60" r="3.4" fill={LIME} stroke="none" />
    </>
  ),
  // K'at (key kan, Yucatec K'an) — Net: a knotted mesh bag.
  kan: (
    <>
      <path d="M30 34 Q50 26 70 34 L64 72 Q50 78 36 72 Z" />
      <path d="M40 31 L44 75 M50 29 V77 M60 31 L56 75" opacity="0.75" />
      <path d="M32 46 Q50 52 68 46 M34 59 Q50 65 66 59" opacity="0.75" />
    </>
  ),
  // Kan (key chikchan) — Serpent: an undulating body, head, forked tongue.
  chikchan: (
    <>
      <path d="M24 62 C36 44, 50 76, 62 56 C68 46, 72 50, 72 42" />
      <circle cx="72" cy="36" r="5" />
      <path d="M76 33 l7 -3 M76 39 l7 3" />
      <circle cx="71" cy="35" r="1.4" fill={LIME} stroke="none" />
    </>
  ),
  // Kame (Kimi) — Death / the ancestors: a calm skull, eyes closed.
  kimi: (
    <>
      <path d="M32 50 a18 18 0 1 1 36 0 v7 a4 4 0 0 1 -4 4 h-28 a4 4 0 0 1 -4 -4 Z" />
      <path d="M39 48 q5 5 10 0" />
      <path d="M51 48 q5 5 10 0" />
      <path d="M47 59 h6" />
    </>
  ),
  // Kej (Manik') — Deer: antlers rising from a head.
  manik: (
    <>
      <circle cx="50" cy="64" r="7" />
      <path d="M50 57 C45 47, 41 49, 37 40 M45 50 q-6 -2 -10 -9" />
      <path d="M50 57 C55 47, 59 49, 63 40 M55 50 q6 -2 10 -9" />
    </>
  ),
  // Q'anil (key lamat) — Seed: a seed sending up a sprout.
  lamat: (
    <>
      <ellipse cx="50" cy="70" rx="12" ry="15" />
      <path d="M50 56 V38" />
      <path d="M50 48 q-13 -3 -17 -14" />
      <path d="M50 44 q13 -3 17 -14" />
    </>
  ),
  // Toj (key muluk) — Offering: a flame over a small altar stone.
  muluk: (
    <>
      <path d="M50 28 C40 42, 38 52, 50 60 C62 52, 60 42, 50 28 Z" />
      <path d="M50 44 C46 50, 47 55, 50 57 C53 55, 54 50, 50 44 Z" fill={LIME} stroke="none" opacity="0.8" />
      <path d="M32 64 H68 L64 74 H36 Z" fill={JADE} fillOpacity="0.18" />
    </>
  ),
  // Tz'i' (Ok) — Dog: a paw print.
  ok: (
    <>
      <ellipse cx="50" cy="60" rx="14" ry="11" />
      <circle cx="33" cy="44" r="5" />
      <circle cx="45" cy="37" r="5" />
      <circle cx="55" cy="37" r="5" />
      <circle cx="67" cy="44" r="5" />
    </>
  ),
  // B'atz' (Chuwen) — Thread / monkey: a spiral thread.
  chuwen: (
    <path d="M50 50 C50 46 56 46 56 50 C56 57 47 57 47 50 C47 41 61 41 61 50 C61 62 43 62 43 50 C43 37 67 37 67 50" />
  ),
  // E (Eb') — Road: a road receding upward.
  eb: (
    <>
      <path d="M34 76 L48 28" />
      <path d="M66 76 L52 28" />
      <path d="M50 70 V65 M50 56 V51 M50 42 V37" opacity="0.6" />
    </>
  ),
  // Aj (B'en) — Cane / home: a cornstalk with leaves.
  ben: (
    <>
      <path d="M50 78 V24" />
      <path d="M50 62 q-13 -2 -17 -12" />
      <path d="M50 50 q13 -2 17 -12" />
      <path d="M50 40 q-12 -2 -15 -11" />
      <path d="M50 24 l-5 -6 M50 24 l5 -6" />
    </>
  ),
  // I'x (Ix) — Jaguar: rosette spots.
  ix: (
    <>
      <circle cx="38" cy="42" r="8" />
      <circle cx="38" cy="42" r="2" fill={LIME} stroke="none" />
      <circle cx="62" cy="42" r="8" />
      <circle cx="62" cy="42" r="2" fill={LIME} stroke="none" />
      <circle cx="50" cy="62" r="8" />
      <circle cx="50" cy="62" r="2" fill={LIME} stroke="none" />
    </>
  ),
  // Tz'ikin (Men) — Bird: swept wings, a head with beak, a fanned tail.
  men: (
    <>
      <path d="M22 50 C34 42, 44 47, 50 53 C56 47, 66 42, 78 50" />
      <circle cx="50" cy="47" r="4.5" />
      <path d="M53 46 l5 -2" />
      <path d="M50 53 L44 72 L50 66 L56 72 Z" fill={JADE} fillOpacity="0.2" />
    </>
  ),
  // Ajmaq (Kib') — Owl / forgiveness: a face with two great eyes.
  kib: (
    <>
      <path d="M30 46 a20 20 0 1 1 40 0 v4 a20 20 0 0 1 -40 0 Z" />
      <circle cx="41" cy="47" r="7" />
      <circle cx="41" cy="47" r="2.4" fill={LIME} stroke="none" />
      <circle cx="59" cy="47" r="7" />
      <circle cx="59" cy="47" r="2.4" fill={LIME} stroke="none" />
      <path d="M50 55 l-4 6 h8 Z" fill={LIME} stroke="none" />
      <path d="M32 30 l7 6 M68 30 l-7 6" />
    </>
  ),
  // No'j (key kaban) — Thought: a head in profile with a spiral of thought.
  kaban: (
    <>
      <path d="M40 76 V66 C30 62, 28 50, 32 42 C37 30, 52 26, 62 32 C70 37, 72 46, 70 52 L74 58 L68 60 V66 H58 V76" />
      <path d="M52 46 C52 41, 60 41, 60 47 C60 53, 50 54, 48 47" opacity="0.8" />
    </>
  ),
  // Tijax (Etz'nab') — Flint: a blade split down its axis.
  etznab: (
    <>
      <path d="M50 26 L66 50 L50 74 L34 50 Z" />
      <path d="M50 26 V74" opacity="0.7" />
      <path d="M41 42 L50 50 L41 58 M59 42 L50 50 L59 58" opacity="0.5" />
    </>
  ),
  // Kawoq (Kawak) — Storm: a cloud over lightning and rain.
  kawak: (
    <>
      <path d="M30 52 a10 10 0 0 1 9 -17 a12 12 0 0 1 23 3 a9 9 0 0 1 2 14 Z" />
      <path d="M48 56 L42 68 L50 66 L44 80" />
      <path d="M60 58 V67 M54 60 V69" opacity="0.5" />
    </>
  ),
  // Ajpu (Ajaw) — Hunter / sun: a radiant sun.
  ajpu: (
    <>
      <circle cx="50" cy="50" r="13" />
      <circle cx="50" cy="50" r="3.6" fill={LIME} stroke="none" />
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return <line key={i} x1={50 + Math.cos(a) * 17} y1={50 + Math.sin(a) * 17} x2={50 + Math.cos(a) * 24} y2={50 + Math.sin(a) * 24} />;
      })}
    </>
  ),
};

function GlyphPaths({ daySign }) {
  const glyph = GLYPHS[daySign] || GLYPHS.imix;
  return (
    <g fill="none" stroke={LIME} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      {glyph}
    </g>
  );
}

// Mayan numeral: dots (1) stacked over bars (5). 7 = two dots over one bar.
export function MayanNumeral({ n, scale = 1 }) {
  const bars = Math.floor(n / 5), dots = n % 5;
  const w = 80 * scale, dotR = 6.5 * scale, gap = 8 * scale, barH = 10 * scale;
  const dotRow = dots > 0 ? dotR * 2 + 6 * scale : 0;
  const totalH = dotRow + (dots > 0 && bars > 0 ? gap : 0) + bars * (barH + gap) + 6 * scale;
  const cx = w / 2, dotsWidth = dots * (dotR * 2) + (dots - 1) * gap;
  return (
    <svg width={w} height={totalH} viewBox={`0 0 ${w} ${totalH}`} style={{ display: "block" }}>
      {Array.from({ length: dots }).map((_, i) => (
        <circle key={i} cx={cx - dotsWidth / 2 + dotR + i * (dotR * 2 + gap)} cy={dotR + 3 * scale} r={dotR} fill={LIME} />
      ))}
      {Array.from({ length: bars }).map((_, i) => (
        <rect key={i} x={w * 0.13} y={dotRow + (dots > 0 ? gap : 0) + i * (barH + gap)} width={w * 0.74} height={barH} rx={2.5} fill={LIME} />
      ))}
    </svg>
  );
}

// The 13 numbers as a line, the user's tone glowing. 7 sits dead center.
export function MayanToneLine({ n }) {
  const N = 13, x0 = 14, x1 = 266, step = (x1 - x0) / (N - 1);
  return (
    <svg viewBox="0 0 280 34" width="100%" height="34" style={{ display: "block" }}>
      <line x1={x0} y1={17} x2={x1} y2={17} stroke={JADE} strokeWidth="1" opacity="0.35" />
      {Array.from({ length: N }).map((_, i) => {
        const x = x0 + i * step, active = i === n - 1;
        return (
          <g key={i}>
            {active && (
              <circle cx={x} cy={17} r="9" fill="none" stroke={LIME} strokeWidth="1" opacity="0.5">
                <animate attributeName="r" values="7;11;7" dur="3s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;0.15;0.6" dur="3s" repeatCount="indefinite" />
              </circle>
            )}
            <circle cx={x} cy={17} r={active ? 5 : 2.4} fill={active ? LIME : JADE} opacity={active ? 1 : 0.45} />
          </g>
        );
      })}
    </svg>
  );
}

export function MayanGlyph({ daySign = "imix", size = 150, outline = false, bare = false }) {
  const uid = useMemo(() => Math.random().toString(36).slice(2, 8), []);
  const lit = !outline;
  const frameOp = lit ? 1 : 0.45;

  // Bare: just the glyph (no cartouche/ring), scaled large, gentle glow pulse.
  if (bare) {
    return (
      <svg viewBox="0 0 150 150" width={size} height={size} style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
        <defs>
          <filter id={`myg-b-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.4" result="b">
              <animate attributeName="stdDeviation" values="1;2.4;1" dur="4.5s" repeatCount="indefinite" />
            </feGaussianBlur>
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <g filter={`url(#myg-b-${uid})`}>
          <g transform="translate(8,8) scale(1.34)"><GlyphPaths daySign={daySign} /></g>
        </g>
      </svg>
    );
  }

  const css = `
    .myg-spin-${uid}{transform-box:fill-box;transform-origin:center;animation:myg-spin-${uid} 90s linear infinite;}
    @keyframes myg-spin-${uid}{to{transform:rotate(360deg);}}
  `;

  // 20 ticks on the rotating ring, a nod to the 20 day signs.
  const ticks = Array.from({ length: 20 }, (_, i) => (i / 20) * Math.PI * 2);

  return (
    <svg viewBox="0 0 150 150" width={size} height={size} style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
      <defs>
        <radialGradient id={`myg-shade-${uid}`} cx="50%" cy="38%" r="70%">
          <stop offset="58%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.35" />
        </radialGradient>
        <filter id={`myg-glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2.2" result="b">
            {lit && <animate attributeName="stdDeviation" values="1.6;3.4;1.6" dur="4.5s" repeatCount="indefinite" />}
          </feGaussianBlur>
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <style>{css}</style>

      <g filter={lit ? `url(#myg-glow-${uid})` : undefined}>
        {/* slowly rotating notched ring */}
        <g className={`myg-spin-${uid}`} opacity={lit ? 0.7 : 0.3}>
          <circle cx="75" cy="75" r="70" fill="none" stroke={JADE} strokeWidth="1" opacity="0.5" />
          {ticks.map((a, i) => {
            const x1 = 75 + Math.cos(a) * 66, y1 = 75 + Math.sin(a) * 66;
            const x2 = 75 + Math.cos(a) * 70, y2 = 75 + Math.sin(a) * 70;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={JADE} strokeWidth={i % 5 === 0 ? 2 : 1} opacity={i % 5 === 0 ? 0.9 : 0.5} />;
          })}
        </g>

        {/* carved cartouche */}
        <rect x="18" y="18" width="114" height="114" rx="24" fill="#1C2128" stroke={JADE} strokeWidth="2.5" opacity={frameOp} />
        <rect x="25" y="25" width="100" height="100" rx="19" fill="none" stroke={LIME} strokeWidth="1" opacity={lit ? 0.4 : 0.2} />
        {[[30,30],[120,30],[30,120],[120,120]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r="3" fill={JADE} opacity={lit ? 0.8 : 0.4} />
        ))}
        <rect x="25" y="25" width="100" height="100" rx="19" fill={`url(#myg-shade-${uid})`} />

        {/* day-sign glyph, sized to fill the cartouche interior */}
        <g transform="translate(28,28) scale(0.94)" opacity={lit ? 0.95 : 0.5}>
          <GlyphPaths daySign={daySign} />
        </g>
      </g>
    </svg>
  );
}
